import { useMoneyFormatter } from "@/lib/money-format";
import { useEffect, useMemo, useRef, useState } from "react";
import { Link, createFileRoute, useNavigate } from "@tanstack/react-router";
import {
  CheckCircle2,
  Clock,
  Loader2,
  Pencil,
  Trash2,
  XCircle,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { RequireAuth } from "@/components/RequireAuth";
import { ErrorState } from "@/components/ErrorState";
import { AppShell } from "@/components/layout/AppShell";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { ProductLookup } from "@/components/shared";
import {
  useCancelCount,
  useCaptureCountLine,
  useCount,
  usePostCount,
  useUpdateCount,
} from "@/lib/api/inventory.queries";
import { useActiveCompany } from "@/lib/company-context";
import { isStaleDraftError, translateDraftError } from "@/lib/draft-errors";
import { useTeamPermission } from "@/lib/team-context";
import {
  createRevisionQueue,
  parseCountedInput,
} from "@/components/inventory/count-edit";
import {
  COUNT_STATUS_LABEL,
  COUNT_STATUS_TONE,
  fmtDate,
  fmtQty,
  fmtSignedQty,
} from "@/components/inventory/format";

export const Route = createFileRoute(
  "/companies/$companyId/inventario/conteos/$countId",
)({
  component: CountDetailPage,
});

function CountDetailPage() {
  return (
    <RequireAuth>
      <AppShell>
        <CountDetailContent />
      </AppShell>
    </RequireAuth>
  );
}

function CountDetailContent() {
  const fmtCop = useMoneyFormatter();
  const { companyId, countId } = Route.useParams();
  const navigate = useNavigate();
  const { setActiveCompanyId } = useActiveCompany();
  useEffect(() => {
    if (companyId) setActiveCompanyId(companyId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  const countQuery = useCount(companyId, countId);
  const captureLine = useCaptureCountLine(companyId, countId);
  const updateCount = useUpdateCount(companyId, countId);
  const postCount = usePostCount(companyId);
  const cancelCount = useCancelCount(companyId);
  const { t } = useTranslation();
  const canWrite = useTeamPermission("inventory.write");

  const [draft, setDraft] = useState<Record<number, string>>({});
  const [editingNotes, setEditingNotes] = useState<string | null>(null);
  // This editor's saves are chained on the revision the server answers, so two quick consecutive
  // captures never conflict with each other, while a save by someone else does (DRAFT_STALE).
  const queue = useRef(createRevisionQueue(0));
  useEffect(() => {
    if (countQuery.data) queue.current.observe(countQuery.data.revision);
  }, [countQuery.data]);

  /** A stale count says so and offers the reload; any other failure shows its own message. */
  function reportFailure(title: string, err: unknown) {
    const draftMessage = translateDraftError(err, t);
    if (draftMessage) {
      toast.error(
        draftMessage,
        isStaleDraftError(err)
          ? {
              action: {
                label: "Recargar conteo",
                onClick: () => void reloadCount(),
              },
            }
          : undefined,
      );
      return;
    }
    toast.error(title, {
      description: err instanceof Error ? err.message : "Error inesperado",
    });
  }
  async function reloadCount() {
    const fresh = await countQuery.refetch();
    if (fresh.data) queue.current.reset(fresh.data.revision);
    setDraft({});
    setEditingNotes(null);
  }
  function saveLine(lineId: number, qtyCounted: number | null) {
    queue.current
      .run(async (revision) => {
        const r = await captureLine.mutateAsync({
          lineId,
          qtyCounted,
          expectedRevision: revision,
        });
        return { result: r, revision: r.revision };
      })
      .catch((err: unknown) => reportFailure("No pudimos guardar", err));
  }
  function editCount(
    input: {
      notes?: string | null;
      addProductIds?: string[];
      removeLineIds?: number[];
    },
    onDone?: () => void,
  ) {
    queue.current
      .run(async (revision) => {
        const saved = await updateCount.mutateAsync({
          ...input,
          expectedRevision: revision,
        });
        return { result: saved, revision: saved.revision };
      })
      .then(() => onDone?.())
      .catch((err: unknown) => reportFailure("No pudimos guardar", err));
  }

  const lines = useMemo(
    () => countQuery.data?.lines ?? [],
    [countQuery.data?.lines],
  );
  const progress = useMemo(() => {
    const captured = lines.filter((l) => l.qtyCounted !== null).length;
    const total = lines.length;
    const pct = total > 0 ? Math.round((captured / total) * 100) : 0;
    return { captured, total, pct };
  }, [lines]);

  const isOpen =
    countQuery.data?.status === "draft" ||
    countQuery.data?.status === "in_progress";
  // An open count that a post has claimed is read-only until the post finishes.
  const canEdit = isOpen && canWrite && countQuery.data?.editable === true;
  const posting = isOpen && countQuery.data?.editable === false;

  if (countQuery.isPending) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );
  }
  if (countQuery.isError || !countQuery.data) {
    return (
      <div className="mx-auto max-w-3xl pt-6">
        <ErrorState
          title="No pudimos cargar el conteo"
          description={countQuery.error?.message ?? ""}
          onRetry={() => void countQuery.refetch()}
          retryLabel="Reintentar"
        />
      </div>
    );
  }

  const count = countQuery.data;

  return (
    <div className="mx-auto flex max-w-6xl flex-col gap-6">
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div className="space-y-1.5">
          <Link
            to="/companies/$companyId/inventario/conteos"
            params={{ companyId }}
            className="inline-flex w-fit items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
          >
            ← Volver a Conteos
          </Link>
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="text-2xl font-semibold tracking-tight">
              Conteo en {count.warehouseName ?? count.warehouseCode ?? "?"}
            </h1>
            <Badge variant={COUNT_STATUS_TONE[count.status]}>
              {COUNT_STATUS_LABEL[count.status]}
            </Badge>
          </div>
          <p className="text-sm text-muted-foreground">
            Iniciado el {fmtDate(count.startedAt)}
            {count.postedAt && ` · Posteado el ${fmtDate(count.postedAt)}`}
            {count.notes && ` · ${count.notes}`}
          </p>
          {canEdit && editingNotes === null && (
            <Button
              variant="ghost"
              size="sm"
              className="h-7 px-2 text-xs"
              onClick={() => setEditingNotes(count.notes ?? "")}
            >
              <Pencil className="mr-1 h-3 w-3" />
              Editar notas
            </Button>
          )}
          {canEdit && editingNotes !== null && (
            <div className="flex items-center gap-2">
              <Input
                aria-label="Notas del conteo"
                className="h-8 w-72"
                maxLength={1000}
                value={editingNotes}
                onChange={(e) => setEditingNotes(e.target.value)}
              />
              <Button
                size="sm"
                disabled={updateCount.isPending}
                onClick={() =>
                  editCount(
                    {
                      notes:
                        editingNotes.trim() === "" ? null : editingNotes.trim(),
                    },
                    () => setEditingNotes(null),
                  )
                }
              >
                Guardar
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setEditingNotes(null)}
              >
                Cancelar
              </Button>
            </div>
          )}
        </div>
        <div className="flex flex-wrap gap-2">
          {isOpen && (
            <>
              <Button
                variant="outline"
                disabled={cancelCount.isPending || !canWrite || posting}
                onClick={() => {
                  if (
                    !confirm(
                      "¿Cancelar este conteo? Se perderán las capturas y no se generarán ajustes.",
                    )
                  )
                    return;
                  cancelCount.mutate(
                    {
                      countId: count.id,
                      expectedRevision: queue.current.revision,
                    },
                    {
                      onSuccess: () => {
                        toast.success("Conteo cancelado");
                        void navigate({
                          to: "/companies/$companyId/inventario/conteos",
                          params: { companyId },
                        });
                      },
                      onError: (err) =>
                        reportFailure("No pudimos cancelar", err),
                    },
                  );
                }}
              >
                <XCircle className="h-4 w-4" />
                Cancelar conteo
              </Button>
              <Button
                disabled={postCount.isPending || !canWrite || posting}
                onClick={() => {
                  if (
                    !confirm(
                      "¿Postear el conteo? Se generarán los ajustes de inventario y sus asientos contables. Esta acción es definitiva.",
                    )
                  )
                    return;
                  // Post exactly the count this editor last saw: wait for its own pending saves, then
                  // send their revision — a count someone else edited since is refused, not posted.
                  void queue.current.idle().then(() =>
                    postCount.mutate(
                      {
                        countId: count.id,
                        expectedRevision: queue.current.revision,
                      },
                      {
                        onSuccess: (r) => {
                          toast.success(
                            `Posteado: ${r.movementsRecorded} ajuste(s), ${r.zeroVariance} sin diferencia, ${r.notCaptured} sin contar.`,
                          );
                        },
                        onError: (err) =>
                          reportFailure("No pudimos postear", err),
                      },
                    ),
                  );
                }}
              >
                {postCount.isPending && (
                  <Loader2 className="h-4 w-4 animate-spin" />
                )}
                <CheckCircle2 className="h-4 w-4" />
                Postear conteo
              </Button>
            </>
          )}
        </div>
      </header>

      {posting && (
        <Card>
          <CardContent className="py-3 text-sm text-amber-700 dark:text-amber-300">
            <Loader2 className="mr-1.5 inline-block h-4 w-4 animate-spin" />
            Este conteo se está posteando. No se puede editar ni cancelar hasta
            que termine.
          </CardContent>
        </Card>
      )}

      {isOpen && (
        <Card>
          <CardContent className="flex flex-col gap-2 py-3">
            <div className="flex items-center justify-between text-sm">
              <span className="font-medium">
                <Clock className="mr-1.5 inline-block h-4 w-4 text-muted-foreground" />
                Progreso de captura
              </span>
              <span className="tabular-nums text-muted-foreground">
                {progress.captured} / {progress.total} ({progress.pct}%)
              </span>
            </div>
            <div className="h-2 overflow-hidden rounded-full bg-muted">
              <div
                className="h-full rounded-full bg-primary transition-all"
                style={{ width: `${progress.pct}%` }}
              />
            </div>
            <p className="text-xs text-muted-foreground">
              Captura las cantidades contadas; al postear se calcula la varianza
              contra el saldo en vivo y se generan los ajustes correspondientes.
            </p>
          </CardContent>
        </Card>
      )}

      {canEdit && (
        <Card>
          <CardContent className="flex flex-col gap-2 py-3">
            <span className="text-sm font-medium">
              Agregar producto al conteo
            </span>
            <ProductLookup
              companyId={companyId}
              value={null}
              onChange={(product) => {
                if (!product) return;
                if (!product.isStockable) {
                  toast.error("Solo se cuentan productos inventariables");
                  return;
                }
                editCount({ addProductIds: [product.id] });
              }}
              placeholder="Buscar producto inventariable…"
            />
          </CardContent>
        </Card>
      )}

      <Card>
        {lines.length === 0 ? (
          <div className="flex flex-col items-center gap-2 py-12 text-center">
            <p className="text-sm text-muted-foreground">
              Este conteo no tiene productos. Probablemente la bodega no tenía
              saldo al iniciarlo.
            </p>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Producto</TableHead>
                <TableHead className="text-right">Saldo al iniciar</TableHead>
                <TableHead className="text-right">Saldo actual</TableHead>
                <TableHead className="text-right">Cantidad contada</TableHead>
                <TableHead className="text-right">Varianza</TableHead>
                <TableHead className="text-right">Valor varianza</TableHead>
                {canEdit && <TableHead className="w-10" />}
              </TableRow>
            </TableHeader>
            <TableBody>
              {lines.map((l) => {
                const isCaptured = l.qtyCounted !== null;
                const liveDraft =
                  draft[l.lineId] ??
                  (l.qtyCounted !== null ? String(l.qtyCounted) : "");
                const variance = l.variance;
                return (
                  <TableRow key={l.lineId}>
                    <TableCell>
                      <Link
                        to="/companies/$companyId/productos/$productId"
                        params={{ companyId, productId: l.productId }}
                        className="font-medium hover:underline"
                      >
                        {l.productName ?? l.productId.slice(0, 8)}
                      </Link>
                      {l.productSku && (
                        <span className="ml-2 font-mono text-xs text-muted-foreground">
                          {l.productSku}
                        </span>
                      )}
                    </TableCell>
                    <TableCell className="text-right tabular-nums text-muted-foreground">
                      {fmtQty(l.qtySystemAtStart)}
                    </TableCell>
                    <TableCell className="text-right tabular-nums text-muted-foreground">
                      {fmtQty(l.qtySystemAtPost)}
                    </TableCell>
                    <TableCell className="text-right">
                      {canEdit ? (
                        <Input
                          type="number"
                          min="0"
                          step="0.000001"
                          className="ml-auto w-24 text-right"
                          value={liveDraft}
                          onChange={(e) =>
                            setDraft((prev) => ({
                              ...prev,
                              [l.lineId]: e.target.value,
                            }))
                          }
                          onBlur={(e) => {
                            // An emptied field clears the capture; an unchanged one writes nothing.
                            const action = parseCountedInput(
                              e.target.value,
                              l.qtyCounted,
                            );
                            if (action.kind === "invalid") {
                              toast.error("Cantidad inválida");
                              return;
                            }
                            if (action.kind === "set")
                              saveLine(l.lineId, action.qtyCounted);
                            else if (action.kind === "clear")
                              saveLine(l.lineId, null);
                          }}
                        />
                      ) : (
                        <span
                          className={`tabular-nums ${
                            isCaptured ? "" : "text-muted-foreground italic"
                          }`}
                        >
                          {isCaptured ? fmtQty(l.qtyCounted) : "Sin contar"}
                        </span>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      {variance === null ? (
                        <span className="text-muted-foreground">—</span>
                      ) : variance === 0 ? (
                        <Badge variant="muted">0</Badge>
                      ) : variance > 0 ? (
                        <Badge
                          variant="success"
                          className="font-mono tabular-nums"
                        >
                          {fmtSignedQty(variance)}
                        </Badge>
                      ) : (
                        <Badge
                          variant="warning"
                          className="font-mono tabular-nums"
                        >
                          {fmtSignedQty(variance)}
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {l.varianceValue === null ? "—" : fmtCop(l.varianceValue)}
                    </TableCell>
                    {canEdit && (
                      <TableCell className="text-right">
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          aria-label="Quitar del conteo"
                          title="Quitar del conteo"
                          disabled={updateCount.isPending}
                          onClick={() =>
                            editCount({ removeLineIds: [l.lineId] })
                          }
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    )}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </Card>
    </div>
  );
}
